import type { ReactNode } from 'react';
import { MoreHoriz } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { configureCardSizeClassName } from 'constant/configure-layout';

type ResourceCardProps = {
  title: string;
  /** Small line under the title (e.g. agent role). */
  subtitle?: string;
  description?: string;
  /** Top-left logo / avatar. Use `ResourceCardLogo` for the default image-or-initial box. */
  icon?: ReactNode;
  /** Rendered next to the icon (e.g. a visibility badge). */
  badge?: ReactNode;
  /** Top-right controls (menu, owner badge, switches). Clicks/keys here do not select the card. */
  actions?: ReactNode;
  /** Extra body content rendered after the description. */
  children?: ReactNode;
  /** Pinned to the bottom of the card. */
  footer?: ReactNode;
  /** When provided the card becomes a keyboard-accessible button. */
  onSelect?: () => void;
  className?: string;
};

export function ResourceCard({
  title,
  subtitle,
  description,
  icon,
  badge,
  actions,
  children,
  footer,
  onSelect,
  className,
}: ResourceCardProps) {
  const isInteractive = Boolean(onSelect);
  const heading = (
    <h3
      className={cn(
        'min-w-0 truncate text-base font-semibold leading-[1.25] text-grey-700 dark:text-foreground',
        icon && 'mt-4'
      )}
      title={title}
    >
      {title}
    </h3>
  );

  return (
    <Card
      className={cn(
        'group flex flex-col rounded-2xl border border-grey-100 bg-white p-5 shadow-none',
        'dark:border-border dark:bg-card',
        configureCardSizeClassName,
        isInteractive && 'cursor-pointer',
        className
      )}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (!onSelect || event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {icon || badge || actions ? (
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {/* Without an icon the title takes the top row, so there is no empty gap above it. */}
              {icon ?? heading}
              {badge}
            </div>
            {actions ? (
              <div
                className="flex shrink-0 items-center gap-1"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {actions}
              </div>
            ) : null}
          </div>
        ) : null}

        {icon || !(badge || actions) ? heading : null}
        {subtitle ? (
          <p
            className="mt-0.5 min-w-0 truncate text-xs font-normal leading-[1.4] text-grey-500 dark:text-muted-foreground"
            title={subtitle}
          >
            {subtitle}
          </p>
        ) : null}
        {description ? (
          <p
            className="mt-1 line-clamp-3 min-h-[3.6rem] min-w-0 text-sm font-normal leading-[1.5] text-grey-600 dark:text-muted-foreground"
            title={description}
          >
            {description}
          </p>
        ) : null}
        {children}

        {footer ? <div className="mt-auto shrink-0 pt-4">{footer}</div> : null}
      </div>
    </Card>
  );
}

type ResourceCardLogoProps = {
  src?: string | null;
  name: string;
  /** Icon to show instead of the initial when there is no `src`. */
  children?: ReactNode;
  className?: string;
};

/** Image when `src` is set, otherwise `children` (an icon) or the first letter of `name`. */
export function ResourceCardLogo({ src, name, children, className }: ResourceCardLogoProps) {
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-indigo-50 p-1.5 text-indigo-600 dark:bg-blue-950/40 dark:text-indigo-300',
        className
      )}
    >
      {src ? (
        <img src={src} alt={name} className="h-full w-full rounded-lg object-contain" />
      ) : (
        children ?? <span className="text-sm font-semibold">{name.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );
}

export type ResourceCardMenuItem = {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

type ResourceCardMenuProps = {
  /** Falsy entries are skipped so callers can write `isOwner && { ... }`. */
  items: Array<ResourceCardMenuItem | false | null | undefined>;
  ariaLabel?: string;
};

/** "More" (⋯) dropdown for the card's `actions` slot. */
export function ResourceCardMenu({ items, ariaLabel }: ResourceCardMenuProps) {
  const { t } = useTranslation();
  const label = ariaLabel || t('MORE_OPTIONS', { defaultValue: 'More options' });
  const visibleItems = items.filter((item): item is ResourceCardMenuItem => Boolean(item));
  if (visibleItems.length === 0) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={label}
                className="h-7 w-7 shrink-0 rounded-md text-grey-500 hover:bg-grey-50 hover:text-grey-700 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-white/70 dark:hover:bg-white/10"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHoriz width={20} height={20} strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
          </DropdownMenuTrigger>
          <TooltipContent side="top">{label}</TooltipContent>
          <DropdownMenuContent
        align="start"
        side="right"
        sideOffset={6}
        className="w-[199px] min-w-[199px] dark:border-grey-900 dark:bg-grey-925"
      >
        {visibleItems.map((item) => (
          <DropdownMenuItem
            key={item.label}
            title={item.tooltip}
            disabled={item.disabled}
            className={cn(
              'rounded-full',
              item.destructive
                ? cn(dropdownMenuItemDestructiveClass, '[&>svg]:!size-5')
                : 'dark:text-grey-100 dark:[&>svg]:text-brand-50 [&>svg]:!size-6'
            )}
            onClick={(event) => {
              event.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>
    </TooltipProvider>
  );
}

export type { ResourceCardProps, ResourceCardLogoProps, ResourceCardMenuProps };
